import { Component, Input, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { SkillVerificationResponseDto } from '../../models/skill-verification-response-dto';
import { PanelList } from '../../../shared/models/panel-list';
import { MessageService } from 'primeng/api';
import { CommonService } from '../../../shared/services/common.service';
import { MySkillVerificationsService } from '../../services/my-skill-verifications.service';
import { FilterMenu } from '../../../shared/models/filter-menu';
import { SortMenu } from '../../../shared/models/sort-menu';
import { MySkillVerificationsConstants } from '../../constants/my-skill-verifications.constants';
import { LinkedTrainingItems } from '../../../shared/models/linkedTrainingItems';
import { UsersService } from '../../../shared/services/users.service';
import { SpinnerService } from '../../../shared/services/spinner.service';

@Component({
  selector: 'app-my-skill-verifications-list',
  templateUrl: './my-skill-verifications-list.component.html',
  styleUrls: ['./my-skill-verifications-list.component.scss']
})
export class MySkillVerificationsListComponent {
  @Input() showLoader: boolean = false;
  @Input() skillVerifications: SkillVerificationResponseDto[] | undefined = undefined;
  @Input() isShowCheckbox: boolean = false;
  @Input() selectCheckbox: boolean = false;
  @Input() enableInActiveLabel: boolean = false;
  @Input() showInformationIconCheckbox: boolean = false;
  @Input() informationIconTooltip: string | undefined = undefined;
  @Input() checkboxLabel: string = 'Show All';

  @Output() checkboxSelection = new EventEmitter<boolean>();
  @Output() reloadMySkillVerifications = new EventEmitter<boolean>();
  readonly linkedTrainingItemsPreviewCount = MySkillVerificationsConstants.MySkillVerifications_LinkedTrainingItemsPreviewCount;
  skillVerificationPanelList: PanelList = {
    title: undefined,
    lists: [],
    enableFilter: true,
    enableSort: true,
    filters: [],
    sort: [],
    enableSelection: true,
    isCheckBoxContainsDisable: true,
    enableToggling: true
  };
  noData: boolean = false;
  selectedPanels: SkillVerificationResponseDto[] = [];
  denyReasonDialogVisible: boolean = false;
  revocationDialogVisible: boolean = false;
  selectedSkillForRemoval: any;

  constructor(private router: Router,
    private messageService: MessageService,
    private commonService: CommonService,
    private mySkillVerificationService: MySkillVerificationsService,
    private userService: UsersService,
    private spinnerService: SpinnerService) {
  }
  ngOnChanges(): void {
    this.setPanelList();
  }


  setPanelList(): void {
    this.skillVerificationPanelList.lists = (this.skillVerifications || []).map(skill => ({
      userAssignedSkillId: skill.userAssignedSkillId,
      userFullName: skill.userFullName,
      skillCode: skill.skillCode,
      skillName: skill.skillName,
      status: skill.status,
      date: skill.date,
      isManagerAssigned: skill.isManagerAssigned,
      linkedTrainingItems: skill.linkedTrainingItems,
      statusDisplay: this.displayStatus(skill.status),
      isActive: this.isRowSelecteableForVerification(skill) ? 'Yes' : 'No'
    }));
    this.skillVerificationPanelList.filters = this.getFiltersForSkillVerificationsPanelList();
    this.skillVerificationPanelList.sort = this.getSortFiledsForSkillVerificationsPanelList();
    this.noData = this.commonService.IsListEmpty(this.skillVerificationPanelList.lists);
    this.selectedPanels = [];
    this.skillVerificationPanelList = { ...this.skillVerificationPanelList, clearSelection: true };
  }
  getSelectedItems(selectedItemsData: SkillVerificationResponseDto[]): void {
    this.selectedPanels = selectedItemsData;
  }
  checkboxSelectionChange(): void {
    this.checkboxSelection.emit(this.selectCheckbox);
  }

  viewSkillDetails(skillCode: string): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree([MySkillVerificationsConstants.MySkillVerifications_Route_SkillDetails + skillCode])
    );
    window.open(url, MySkillVerificationsConstants.MySkillVerifications_Window_Target);
  }
  getPreviewLinkedTrainingItems(skill: SkillVerificationResponseDto): LinkedTrainingItems[] {
    return skill?.linkedTrainingItems?.slice(0, this.linkedTrainingItemsPreviewCount) || [];
  }
  getLinkedTrainingItemText(item: LinkedTrainingItems): string {
    const version = item.version ? ` (v${item.version})` : '';
    const status = item.trainingStatus || 'N/A';

    return `${item.number || ''} ${item.title || ''}${version} - ${status}`.trim();
  }
  getRemainingLinkedTrainingItems(skill: SkillVerificationResponseDto): LinkedTrainingItems[] {
    return skill?.linkedTrainingItems?.slice(this.linkedTrainingItemsPreviewCount) || [];
  }

  getAllLinkedTrainingItems(skill: SkillVerificationResponseDto): LinkedTrainingItems[] {
    return skill?.linkedTrainingItems || [];
  }

  /* Panel list stamps collapsed on each card, undefined means the card is expanded. */
  isPanelExpanded(skill: SkillVerificationResponseDto): boolean {
    return (skill as any)?.collapsed === false;
  }

  getVisibleLinkedTrainingItems(skill: SkillVerificationResponseDto): LinkedTrainingItems[] {
    return this.isPanelExpanded(skill)
      ? this.getAllLinkedTrainingItems(skill)
      : this.getPreviewLinkedTrainingItems(skill);
  }

  showMoreLinkedTrainingItemsIndicator(skill: SkillVerificationResponseDto): boolean {
    return !this.isPanelExpanded(skill) && this.hasMoreLinkedTrainingItems(skill);
  }

  hasMoreLinkedTrainingItems(skill: SkillVerificationResponseDto): boolean {
    return (skill?.linkedTrainingItems?.length || 0) > this.linkedTrainingItemsPreviewCount;
  }

  displayStatus(status: string): string {
    if (status === MySkillVerificationsConstants.MySkillVerifications_Status_PendingAcknowledgement) {
      return MySkillVerificationsConstants.MySkillVerifications_PendingAcknowledgement;
    }

    if (status === MySkillVerificationsConstants.MySkillVerifications_Status_PendingVerification) {
      return MySkillVerificationsConstants.MySkillVerifications_PendingVerification;
    }

    return status;
  }  
  getStatusColor(status: string): string {
    switch (status) {
      case MySkillVerificationsConstants.MySkillVerifications_Status_PendingAcknowledgement:
        return 'pending-acknowledgement';
      case MySkillVerificationsConstants.MySkillVerifications_Status_PendingVerification:
        return 'pending-verification';
      case MySkillVerificationsConstants.MySkillVerifications_Status_Earned:
        return 'earned';
      case MySkillVerificationsConstants.MySkillVerifications_Status_Removed:
        return 'removed';
      default:
        return '';
    }
  }

  hasLinkedTrainingItems(skill: SkillVerificationResponseDto): boolean {
    return skill?.linkedTrainingItems != null && skill.linkedTrainingItems.length > 0;
  }
  getFiltersForSkillVerificationsPanelList(): FilterMenu[] {
    return [
      {
        filterDisplayName: 'User',
        filterFieldName: 'userFullName',
      },
      {
        filterDisplayName: 'Status',
        filterFieldName: 'statusDisplay',
      }
    ];
  }
  getSortFiledsForSkillVerificationsPanelList(): SortMenu[] {
    return [
      {
        sortFieldDisplayName: 'User',
        sortFieldName: 'userFullName'
      },
      {
        sortFieldDisplayName: 'Skill Code',
        sortFieldName: 'skillCode'
      },
      {
        sortFieldDisplayName: 'Skill Name',
        sortFieldName: 'skillName'
      },
      {
        sortFieldDisplayName: 'Status',
        sortFieldName: 'status'
      },
      {
        sortFieldDisplayName: 'Date',
        sortFieldName: 'date'
      }
    ];
  }
  private isRowSelecteableForVerification(skill: SkillVerificationResponseDto): boolean {
    return this.isPendingVerification(skill);
  }
 
  isPendingVerification(skill: SkillVerificationResponseDto): boolean {
    return skill.status === MySkillVerificationsConstants.MySkillVerifications_Status_PendingVerification;
  }

  /// <summary>
  /// Gets the display name for the selected skill.
  /// Prefers skill name, falls back to skill code, otherwise returns empty string.
  /// </summary>
  get selectedSkillDisplayName(): string {
    return this.selectedSkillForRemoval?.skillName || this.selectedSkillForRemoval?.skillCode || '';
  }

  /// <summary>
  /// Gets the skill code for the selected skill.
  /// Returns empty string if no skill is selected.
  /// </summary>
  get selectedSkillCode(): string {
    return this.selectedSkillForRemoval?.skillCode || '';
  }

  // Verify selected skill verifications
  verifySkills(): void {
    // Step 1: Validate selection
    if (!this.selectedPanels || this.selectedPanels.length === 0) {
      this.messageService.add({
        key: MySkillVerificationsConstants.MySkillVerifications_MessageKey_Toast,
        severity: MySkillVerificationsConstants.MySkillVerifications_Severity_Warning,
        summary: MySkillVerificationsConstants.MySkillVerifications_Msg_NoSkillSelected,
        life: MySkillVerificationsConstants.MySkillVerifications_ToastNotificationLifespan_Ms
      });
      return;
    }

    // Step 2: Extract skill code + user PUID - backend handles all categorization and batching
    const skillsToVerify = this.selectedPanels
      .map(skill => ({
        skillCode: skill.skillCode,
        userPuid: this.extractPuidFromUserFullName(skill.userFullName)
      }))
      .filter(item => item.userPuid !== null);

    if (skillsToVerify.length === 0) {
      this.messageService.add({
        key: MySkillVerificationsConstants.MySkillVerifications_MessageKey_Toast,
        severity: MySkillVerificationsConstants.MySkillVerifications_Severity_Err,
        summary: MySkillVerificationsConstants.MySkillVerifications_Msg_FailedExtractPuid,
        life: MySkillVerificationsConstants.MySkillVerifications_ToastNotificationLifespan_Ms
      });
      return;
    }

    // Step 3: Send to backend for processing
    this.verifySelectedSkills(skillsToVerify as any);
  }

  private verifySelectedSkills(skillsToVerify: any[]): void {
    this.mySkillVerificationService.verifySkills(skillsToVerify).subscribe({
      next: (response: any) => {
        this.messageService.add({
          key: MySkillVerificationsConstants.MySkillVerifications_MessageKey_Toast,
          severity: MySkillVerificationsConstants.MySkillVerifications_Severity_Success,
          summary: response.message || `${skillsToVerify.length} ${MySkillVerificationsConstants.MySkillVerifications_Msg_VerifySuccess}`,
          life: MySkillVerificationsConstants.MySkillVerifications_ToastNotificationLifespan_Ms
        });
          this.reloadMySkillVerifications.emit(false);
          this.selectedPanels = [];
      },
      error: (error) => {
        this.messageService.add({
          key: MySkillVerificationsConstants.MySkillVerifications_MessageKey_Toast,
          severity: MySkillVerificationsConstants.MySkillVerifications_Severity_Err,
          summary: error?.error?.message || MySkillVerificationsConstants.MySkillVerifications_Msg_VerifyFailed,
          life: MySkillVerificationsConstants.MySkillVerifications_ToastNotificationLifespan_Ms
        });
      }
    });
  }

  // Deny selected skill verifications
  denySkills(): void {
    this.selectedSkillForRemoval = this.selectedPanels[0];
    if (this.selectedSkillForRemoval?.isManagerAssigned) {
      // Check if all linked training items are completed
      const allTrainingCompleted = this.areAllTrainingItemsCompleted(this.selectedSkillForRemoval); 
      if (allTrainingCompleted) {
        this.revocationDialogVisible = true;
      } else {
        this.denyReasonDialogVisible = true;
      } 
    } else {
      this.revocationDialogVisible = true;
    }
  }

  private areAllTrainingItemsCompleted(skill: any): boolean {
    if (!skill?.linkedTrainingItems || skill.linkedTrainingItems.length === 0) {
      return false; // No training items to complete
    }
    
    return skill.linkedTrainingItems.every((item: LinkedTrainingItems) => 
      item.trainingStatus?.toLowerCase() === MySkillVerificationsConstants.MySkillVerifications_TrainingStatus_Completed
    );
  }


  onDenyReasonConfirm(reason: string): void {
    if(!this.selectedPanels[0]) {
      return;
    }
    this.DenySelectedSkill(this.selectedPanels[0], reason);
    this.denyReasonDialogVisible = false;
  }

  onDenyReasonCancel(): void {
    this.denyReasonDialogVisible = false;
    this.selectedSkillForRemoval = null;
  }

  onRevocationConfirm(data: { itemIds: string[]; reason: string; nonCompleteItems: any[] }): void {
    this.revocationDialogVisible = false;
    this.revokeTrainingOccurrences(this.selectedSkillForRemoval, data);
  }
 
  private revokeTrainingOccurrences(skill: any, data: { itemIds: string[]; reason: string; nonCompleteItems: any[] }): void {
    const puid = this.extractPuidFromUserFullName(skill.userFullName);
    if (!puid) {
      this.messageService.add({
        key: MySkillVerificationsConstants.MySkillVerifications_MessageKey_Toast,
        severity: MySkillVerificationsConstants.MySkillVerifications_Severity_Err,
        summary: MySkillVerificationsConstants.MySkillVerifications_Msg_ExtractPuidFailed,
        life: MySkillVerificationsConstants.MySkillVerifications_ToastNotificationLifespan_Ms
      });
      return;
    }
    const revocationRequest = {
      userAssignedSkillId: skill.userAssignedSkillId,
      trainingItemIds: data.itemIds,
      revocationReason: data.reason
    };

    this.userService.revokeTrainingOccurrences(puid, revocationRequest).subscribe({
      next: (response: any) => {
        if (response?.nonCompleteTrainingItemIds && response.nonCompleteTrainingItemIds.length > 0) {
          const itemCodes = response.nonCompleteTrainingItemIds.join(', ');
          this.messageService.add({
            key: MySkillVerificationsConstants.MySkillVerifications_MessageKey_Toast,
            severity: MySkillVerificationsConstants.MySkillVerifications_Severity_Warning,
            summary: MySkillVerificationsConstants.MySkillVerifications_Msg_TrainingNotUpdated,
            detail: MySkillVerificationsConstants.MySkillVerifications_Msg_TrainingNotUpdatedDetail + itemCodes,
          });
          return; // Don't proceed further if there are non-complete items
        }
        if (response?.success !== false) {
                  this.messageService.add({
                    key: MySkillVerificationsConstants.MySkillVerifications_MessageKey_Toast,
                    severity: MySkillVerificationsConstants.MySkillVerifications_Severity_Success,
                    summary: MySkillVerificationsConstants.MySkillVerifications_Msg_TrainingRevocationSuccess,
                    detail: MySkillVerificationsConstants.MySkillVerifications_Msg_TrainingRevocationSuccessDetail,
                    life: MySkillVerificationsConstants.MySkillVerifications_ToastNotificationLifespan_Ms
                  });
                   this.selectedPanels = [];
                      this.reloadMySkillVerifications.emit(false);   
                }
      },
      error: (error) => {
        this.messageService.add({
          key: MySkillVerificationsConstants.MySkillVerifications_MessageKey_Toast,
          severity: MySkillVerificationsConstants.MySkillVerifications_Severity_Err,
          summary: MySkillVerificationsConstants.MySkillVerifications_Msg_TrainingRevocationFailed,
          life: MySkillVerificationsConstants.MySkillVerifications_ToastNotificationLifespan_Ms
        });
      }
    });
  }

  private extractPuidFromUserFullName(userFullName: string): string | null {
    if (!userFullName) {
      return null;
    }
    const match = userFullName.match(/\(([^)]+)\)/);
    return match ? match[1] : null;
  }

  onRevocationCancel(): void {
    this.revocationDialogVisible = false;
    this.selectedSkillForRemoval = null;
  }

  private DenySelectedSkill(skill: any, reason: string): void {
      // Extract userPuid from skill.userFullName
      const userPuid = this.extractPuidFromUserFullName(skill.userFullName);
      
      if (!userPuid) {
        this.messageService.add({
          key: MySkillVerificationsConstants.MySkillVerifications_MessageKey_Toast,
          severity: MySkillVerificationsConstants.MySkillVerifications_Severity_Err,
          summary: MySkillVerificationsConstants.MySkillVerifications_Msg_ExtractPuidFailed,
          life: MySkillVerificationsConstants.MySkillVerifications_ToastNotificationLifespan_Ms

        });
        return;
      }

      // Call new deny-userskill endpoint with natural keys (userPuid + skillCode)
      this.userService.denyUserSkill(userPuid, skill.skillCode, reason).subscribe({
        next: () => {
          this.messageService.add({
            key: MySkillVerificationsConstants.MySkillVerifications_MessageKey_Toast,
            severity: MySkillVerificationsConstants.MySkillVerifications_Severity_Success,
            summary: MySkillVerificationsConstants.MySkillVerifications_Msg_RemoveSuccess,
            life: MySkillVerificationsConstants.MySkillVerifications_ToastNotificationLifespan_Ms

          });
           this.selectedPanels = [];
            this.selectedSkillForRemoval = null;
            this.reloadMySkillVerifications.emit(false);          
        },
        error: (error) => {
          this.messageService.add({
            key: MySkillVerificationsConstants.MySkillVerifications_MessageKey_Toast,
            severity: MySkillVerificationsConstants.MySkillVerifications_Severity_Err,
            summary: error?.error?.message || MySkillVerificationsConstants.MySkillVerifications_Msg_RemoveFailed,
            life: MySkillVerificationsConstants.MySkillVerifications_ToastNotificationLifespan_Ms

          });
        }
      });
    }
  
}
